import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { api, IMG, PURE_SOULS_URL } from "@/lib/site";
import SectionHeader from "@/components/site/SectionHeader";
import FeaturedProviderCard from "@/components/site/FeaturedProviderCard";
import ArticleCard from "@/components/site/ArticleCard";
import FaqList from "@/components/site/FaqList";
import CountUp from "@/components/site/CountUp";
import Reveal from "@/components/site/Reveal";
import { Droplets, Leaf, HeartHandshake, Sparkles, ArrowRight } from "lucide-react";
import Seo from "@/components/site/Seo";
import { PAGE_SEO, homepageLd } from "@/lib/seo";

const stats = [
  { value: 9, suffix: "", label: "Verified providers" },
  { value: 7, suffix: "", label: "States covered" },
  { value: 90, suffix: "%", label: "Less energy than flame" },
  { value: 0, suffix: "", label: "Direct emissions" },
];

const homeFaq = [
  {
    q: "What is the difference between water cremation and flame cremation?",
    a: "Water cremation uses warm water and alkaline chemistry to accelerate the natural decomposition process, while traditional cremation uses high heat and flame. The result of both is mineral remains returned to the family, but the journey there is quieter and gentler with water cremation.",
  },
  {
    q: "Is water cremation environmentally friendly?",
    a: "Many families consider water cremation to be a more environmentally conscious option due to its lower emissions, smaller energy footprint, and reduced carbon impact compared to flame cremation.",
  },
  {
    q: "Are pets cremated individually?",
    a: "Most professional water cremation providers offer individual aqua cremation services with identification and tracking systems used throughout the process, so the remains returned are unmistakably your own pet's.",
  },
  {
    q: "What remains are returned after water cremation?",
    a: "The remains returned are clean mineral remains, typically lighter in colour and finer in texture than traditional ash. Many families describe them as feeling softer, almost like fine sand.",
  },
  {
    q: "Is water cremation available throughout Australia?",
    a: "Availability is still developing across Australia, however interest in water cremation continues to grow as more families seek sustainable alternatives. Queensland currently leads in availability through providers such as Pure Souls.",
  },
];

const benefits = [
  {
    icon: Droplets,
    title: "A Gentle Process",
    body: "Water cremation uses a calm water based process inspired by nature and natural decomposition.",
  },
  {
    icon: Leaf,
    title: "Lower Environmental Impact",
    body: "The process produces significantly fewer emissions compared to traditional flame cremation.",
  },
  {
    icon: HeartHandshake,
    title: "Individual Care",
    body: "Each pet is individually tracked and respectfully cared for throughout the entire journey.",
  },
  {
    icon: Sparkles,
    title: "Soft Natural Remains",
    body: "The remains returned are typically lighter in colour and finer in texture than traditional ash.",
  },
];

const processSteps = [
  {
    n: "01",
    title: "Respectful Transfer & Identification",
    body: "Your pet is respectfully received and individually identified before the process begins.",
  },
  {
    n: "02",
    title: "Individual Water Cremation",
    body: "A gentle water based process carefully accelerates natural organic decomposition using warm water and alkaline chemistry.",
  },
  {
    n: "03",
    title: "Careful Processing",
    body: "Remaining minerals are gently dried and processed into a soft memorial powder.",
  },
  {
    n: "04",
    title: "Return To Family",
    body: "Your pet's remains are respectfully returned to the family in the selected memorial option.",
  },
];

export default function Home() {
  const [articles, setArticles] = useState([]);
  const heroRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const heroImageY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  useEffect(() => {
    api
      .get("/articles", { params: { limit: 4 } })
      .then((res) => setArticles(res.data.filter((a) => !a.is_stub).slice(0, 3)))
      .catch(() => setArticles([]));
  }, []);

  return (
    <div data-testid="home-page">
      <Seo {...PAGE_SEO.home} jsonLd={homepageLd()} />
      {/* ===================== HERO ===================== */}
      <section
        ref={heroRef}
        className="relative isolate overflow-hidden grain"
        data-testid="hero-section"
      >
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <motion.img
            src={IMG.heroFog}
            alt="Calm misty lake at dawn"
            className="absolute inset-x-0 -top-[12%] w-full h-[124%] object-cover"
            style={{ y: reduce ? 0 : heroY }}
          />
          <div className="absolute inset-0 bg-[#F9F9F6]/90" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F9F9F6]/40 via-transparent to-[#F9F9F6]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F9F9F6]/97 via-[#F9F9F6]/80 to-[#F9F9F6]/25" />
        </div>
        <div className="aura bg-sage-300/28 w-[42vw] h-[42vw] -top-[12vw] -left-[12vw]" />
        <div className="aura bg-water-300/38 w-[34vw] h-[34vw] top-[14%] -right-[8vw]" />

        <div className="container-editorial pt-20 sm:pt-28 pb-20 sm:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="overline text-forest-700 mb-6" data-testid="hero-overline">
                A Calm Educational Resource
              </div>
              <h1
                className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-[5.4rem] tracking-tight leading-[1.02] text-forest-950 max-w-4xl"
                data-testid="hero-headline"
              >
                Understanding Pet{" "}
                <br />
                Water Cremation
                <span className="italic text-water-800"> In Australia.</span>
              </h1>
              <p
                className="mt-8 max-w-2xl text-forest-900 text-lg sm:text-xl leading-[1.65]"
                data-testid="hero-subheading"
              >
                A gentle and environmentally conscious approach to pet aftercare.
                Water cremation, also known as aqua cremation or alkaline hydrolysis,
                is a modern alternative to traditional flame cremation that uses
                water and natural processes to care for beloved pets with dignity,
                respect, and compassion.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/the-process"
                  className="btn-primary"
                  data-testid="hero-primary-cta"
                >
                  Learn About The Process
                </Link>
                <Link
                  to="/providers"
                  className="btn-secondary"
                  data-testid="hero-secondary-cta"
                >
                  Find A Provider
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="relative" style={{ perspective: "1200px" }}>
                <div className="absolute -inset-6 -z-10 rounded-[2.75rem] bg-gradient-to-tr from-sage-300/35 via-water-300/45 to-clay-200/25 blur-3xl opacity-70" />
                <motion.div
                  style={{ y: reduce ? 0 : heroImageY }}
                  className="tilt-3d glass-strong rounded-[1.9rem] p-3"
                  data-testid="hero-signature-frame"
                >
                  <div className="rounded-[1.4rem] overflow-hidden">
                    <img
                      src={IMG.australiaWater}
                      alt="A map of Australia formed from clear flowing water, representing water cremation across the country"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </motion.div>
                <div className="absolute -bottom-5 -left-5 hidden sm:block glass-caption rounded-2xl px-4 py-3 max-w-[11rem]">
                  <p className="font-serif text-sm text-forest-900 leading-snug">
                    Compassionate pet aftercare, across Australia.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 sm:mt-20">
            <FeaturedProviderCard variant="inline" />
          </div>
        </div>
      </section>

      {/* ===================== STATS STRIP ===================== */}
      <section className="relative bg-gradient-to-b from-sand-100 to-sand-50 border-y border-white/60" data-testid="home-stats">
        <div className="container-editorial py-14 sm:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {stats.map((s) => (
              <div
                key={s.label}
                className="glass rounded-2xl px-6 py-7 text-center md:text-left transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-card"
              >
                <div className="font-serif text-5xl sm:text-6xl text-forest-950 tracking-tight leading-none">
                  <CountUp to={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-3 text-sm text-forest-600">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== WHAT IS WATER CREMATION ===================== */}
      <section className="section-padding" data-testid="what-is-section">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-7">
              <SectionHeader
                overline="The Basics"
                title="What Is Water Cremation?"
                testId="what-is-header"
              />
              <div className="space-y-6 text-forest-700 text-lg leading-[1.85]">
                <p>
                  Water cremation is a gentle form of pet aftercare that uses
                  warm water, gentle circulation, and an alkaline solution to
                  accelerate the natural process of returning the body to its
                  basic organic elements.
                </p>
                <p>
                  This process is commonly known as <em>aqua cremation</em> or{" "}
                  <em>alkaline hydrolysis</em> and is increasingly recognised
                  around the world as a more sustainable alternative to
                  traditional flame cremation.
                </p>
                <p>
                  Unlike flame based methods, water cremation uses a water based
                  process that many families feel aligns more closely with
                  nature, gentleness, and environmental responsibility.
                </p>
              </div>

              <ul className="mt-10 space-y-4">
                {[
                  "A gentler process",
                  "Lower environmental impact",
                  "Individual care and identification",
                  "Softer, cleaner remains",
                  "A more modern and sustainable option",
                ].map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-4 text-forest-800"
                    data-testid={`what-is-bullet-${b.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-sage-500 shrink-0" />
                    <span className="text-lg">{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10">
                <Link
                  to="/what-is-water-cremation"
                  className="link-underline text-sm"
                  data-testid="what-is-learn-more"
                >
                  Learn more about water cremation →
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="relative">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden">
                  <img
                    src={IMG.dogFluffy}
                    alt="A cat resting by a sunlit window overlooking a calm lake"
                    className="w-full h-full object-cover object-left"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 hidden md:block glass-strong rounded-2xl p-6 max-w-xs">
                  <p className="font-serif italic text-xl text-forest-800 leading-snug">
                    &ldquo;Warmth, water, and time: three quiet elements at the heart
                    of the process.&rdquo;
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== BENEFITS ===================== */}
      <section
        className="relative isolate section-padding bg-sand-50 overflow-hidden"
        data-testid="benefits-section"
      >
        <div className="aura bg-clay-200/28 w-[40vw] h-[40vw] -top-[8vw] right-[-8vw]" />
        <div className="aura bg-water-300/30 w-[36vw] h-[36vw] bottom-[-12vw] left-[-10vw]" />
        <div className="container-editorial relative">
          <SectionHeader
            overline="Why It Matters"
            title="Why families across Australia are exploring water cremation"
            testId="benefits-header"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={Math.min(i * 0.08, 0.4)}>
                <div
                  className="glass-strong card-hover rounded-2xl p-8 h-full"
                  data-testid={`benefit-card-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="h-12 w-12 rounded-full bg-sage-50 border border-sage-100 flex items-center justify-center mb-6 text-sage-700">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-2xl text-forest-900 leading-tight tracking-tight mb-3">
                    {b.title}
                  </h3>
                  <p className="text-forest-700 text-[15px] leading-relaxed">
                    {b.body}
                  </p>
                </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== PROCESS ===================== */}
      <section className="relative isolate section-padding bg-aqua-50 overflow-hidden" data-testid="process-section">
        <div className="aura bg-water-300/36 w-[38vw] h-[38vw] top-[-8vw] left-[-10vw]" />
        <div className="container-editorial relative">
          <SectionHeader
            overline="How It Works"
            title="Understanding the process"
            intro="A calm, considered journey from arrival to the return of remains. Each step is unhurried and respectful."
            testId="process-header"
          />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-7">
              <ol className="relative border-l border-sand-300 pl-10 space-y-12">
                {processSteps.map((s) => (
                  <li
                    key={s.n}
                    className="relative"
                    data-testid={`process-step-${s.n}`}
                  >
                    <span className="absolute -left-[3.4rem] top-0 font-serif text-5xl text-sage-500/80 leading-none">
                      {s.n}
                    </span>
                    <h3 className="font-serif text-2xl sm:text-3xl text-forest-900 leading-tight tracking-tight mb-3">
                      {s.title}
                    </h3>
                    <p className="text-forest-700 text-lg leading-[1.75] max-w-xl">
                      {s.body}
                    </p>
                  </li>
                ))}
              </ol>

              <div className="mt-12">
                <Link
                  to="/the-process"
                  className="btn-secondary"
                  data-testid="process-cta"
                >
                  Read the full process <ArrowRight size={16} />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="relative">
                <div className="rounded-3xl overflow-hidden aspect-[4/5]">
                  <img
                    src={IMG.handPaw}
                    alt="A person gently holding their dog's paw"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 hidden md:block glass-strong rounded-2xl p-6 max-w-xs">
                  <p className="font-serif italic text-xl text-forest-800 leading-snug">
                    &ldquo;Every pet is cared for individually, with dignity at every step.&rdquo;
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== PROVIDERS TEASER ===================== */}
      <section
        className="relative isolate section-padding bg-sand-100 overflow-hidden"
        data-testid="providers-teaser-section"
      >
        <div className="aura bg-water-300/24 w-[38vw] h-[38vw] top-[-8vw] left-[-8vw]" />
        <div className="container-editorial relative">
          <SectionHeader
            overline="Find Care Near You"
            title="Water cremation providers in Australia"
            intro="Services are gradually becoming more available as awareness around sustainable pet aftercare continues to grow. Below is the featured provider currently leading the category."
            testId="providers-teaser-header"
          />
          <FeaturedProviderCard variant="full" />
          <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
            <p className="text-forest-700 text-sm">
              A growing directory of verified providers now spans most states
              across Australia.
            </p>
            <Link
              to="/providers"
              className="link-underline text-sm"
              data-testid="providers-teaser-cta"
            >
              View the directory →
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section className="section-padding" data-testid="faq-section">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-4 lg:sticky lg:top-28">
              <SectionHeader
                overline="Good To Know"
                title="Frequently asked questions"
                intro="Calm, clear answers to the questions families ask most often when first exploring water cremation."
                testId="faq-header"
              />
              <Link
                to="/faq"
                className="link-underline text-sm"
                data-testid="faq-view-all"
              >
                View all questions →
              </Link>
            </div>
            <div className="lg:col-span-8">
              <FaqList items={homeFaq} testIdPrefix="home-faq" />
            </div>
          </div>
        </div>
      </section>

      {/* ===================== LEARNING CENTRE ===================== */}
      <section
        className="relative isolate section-padding bg-sand-100 overflow-hidden"
        data-testid="learning-centre-section"
      >
        <div className="aura bg-water-200/26 w-[40vw] h-[40vw] bottom-[-12vw] right-[-10vw]" />
        <div className="container-editorial relative">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <SectionHeader
              overline="Keep Reading"
              title="Learning Centre"
              intro="Educational, evergreen reading on water cremation, aqua cremation, and sustainable pet aftercare."
              testId="learning-centre-header"
            />
            <Link
              to="/articles"
              className="link-underline text-sm pb-4"
              data-testid="learning-centre-view-all"
            >
              View all articles →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {articles.map((a, i) => (
              <Reveal key={a.slug} delay={Math.min(i * 0.08, 0.4)}>
                <ArticleCard article={a} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== QUIET CLOSING ===================== */}
      <section
        className="section-padding"
        data-testid="closing-section"
      >
        <div className="container-editorial">
          <div className="max-w-3xl">
            <div className="overline mb-6">A Quiet Note</div>
            <p className="font-serif text-3xl sm:text-4xl lg:text-5xl text-forest-900 leading-[1.15] tracking-tight">
              This website exists to educate, not to sell. Take your time. Speak
              with providers. Choose what feels right for your family.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/articles" className="btn-secondary">
                Continue learning
              </Link>
              <a
                href={PURE_SOULS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="closing-pure-souls-link"
              >
                Visit Pure Souls
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
